/**
 * Suivi d'une enveloppe - « l'ecran qui construit la confiance ».
 *
 * Le backend expose deliberement hsm_status, tsa_status, les compteurs de
 * tentatives et les dernieres erreurs : ces champs existent POUR ETRE
 * MONTRES. Un client doit voir OU ca bloque, pas un « en cours » opaque
 * qui le laisse imaginer le pire.
 *
 * On traduit en revanche le vocabulaire interne : « horodatage en cours
 * (2ᵉ tentative) » plutot que `tsa_status=processing, attempt=2`.
 */
import { useCallback, useEffect, useState } from "react";

import { DemandesSignataires } from "../components/DemandesSignataires";
import { api, type Duplication, type EnveloppeDetail as Detail, type EvenementAudit, type Invitation } from "../api";
import { LiensATransmettre } from "../components/LiensATransmettre";
import { Chargement, Erreur, Resultat, Statut, date, telecharger } from "../components/ui";

interface Props {
  envelopeId: string;
  onRetour: () => void;
  /** « Dupliquer » (12/10/2026) : ouvre la creation pre-remplie. */
  onDupliquer?: (depuis: Duplication) => void;
}

/** Traitement avant signature (12/10/2026). */
const STATUTS_INTERVENANT: Record<string, { texte: string; classe: string }> = {
  attente: { texte: "En attente", classe: "pill--muted" },
  invite: { texte: "À faire", classe: "pill--pending" },
  termine: { texte: "Fait", classe: "pill--ok" },
  refuse: { texte: "Refusé", classe: "pill--err" },
};

/** Traduction des evenements d'audit. Ceux qui ne sont pas listes
 *  s'affichent bruts : mieux vaut un nom technique qu'une ligne
 *  disparue - la piste d'audit doit rester complete. */
const EVENEMENTS: Record<string, string> = {
  "envelope.created": "Enveloppe créée",
  "envelope.sent": "Envoi demandé",
  "signer.invitation_sent": "E-mail de signature envoyé",
  "signer.invitation_failed": "E-mail de signature NON envoyé",
  "signer.link_resent": "Nouveau lien demandé",
  "document.signed_link_sent": "Document signé envoyé par e-mail",
  "document.signed_link_failed": "Document signé NON envoyé par e-mail",
  "envelope.completed": "Enveloppe terminée",
  "envelope.cancelled": "Enveloppe annulée",
  "signer.champ_rempli": "Champ complété",
  "signer.document_reviewed": "Document lu jusqu'au bout",
  "signer.piece_deposee": "Pièce déposée",
  "signer.approved": "Approuvé (sans signer)",
  "envelope.traitement_demarre": "Traitement avant signature commencé",
  "envelope.traitement_valide": "Validé avant envoi",
  "envelope.arretee_avant_signature": "Arrêtée avant signature (tarif enveloppe non signée)",
  "signer.viewed": "Document consulté",
  "signer.consent_recorded": "Consentement donné",
  "signer.otp_requested": "Code de vérification envoyé",
  "signer.otp_failed": "Code incorrect saisi",
  "signer.authenticated": "Identité vérifiée",
  "signer.declined": "Signature refusée",
  "signer.signed": "Signature apposée",
  "envelope.expired": "Enveloppe périmée",
  "envelope.technical_failure": "Échec technique de la signature",
  "envelope.failure_email_sent": "E-mail d'échec envoyé",
  "envelope.failure_email_failed": "E-mail d'échec NON envoyé",
  "trust.signature_completed": "Document signé et horodaté",
  "billing.credit_deducted": "Crédit débité",
  "tsa_quota.exceeded": "Quota d'horodatages qualifiés atteint",
  // 14/10/2026 : plus aucun code brut affiche.
  "signer.piece_jointe_deposee": "Pièce déposée",
  "envelope.finalisation_lancee": "Fin du parcours : finalisation et scellement lancés",
  "signer.signature_choisie": "Signature choisie",
  "signer.signature_after_closure": "Signature tentée après la clôture",
  "envelope.intervenant_relance": "Intervenant relancé",
  "envelope.dossier_non_signee_scelle": "Journal de preuve scellé",
  "envelope.fichiers_de_travail_effaces": "Fichiers de travail effacés (archive vérifiée)",
  "envelope.lta_upgrade_completed": "Archivage longue durée renforcé",
  "envelope.lta_upgrade_failed": "Renforcement de l'archivage en échec",
  "archive.replication_failed": "Copie d'archive en échec (reprise programmée)",
  "document.upload_rejected": "Document refusé au dépôt",
};

/**
 * Le message suit l'etat REEL (25/09/2026). Avant, tout echec d'horodatage
 * annoncait « une reprise automatique est programmee », y compris apres
 * l'epuisement des tentatives, quand plus rien ne l'etait ; et tout echec
 * HSM « nos equipes sont alertees », y compris pendant les nouvelles
 * tentatives, quand personne ne l'etait encore. Le serveur dit ce qui est
 * programme (reprise_programmee) ; l'ecran ne le devine plus.
 */
function etapeTechnique(e: Detail): { texte: string; classe: string } | null {
  const echec = e.hsm_status === "failed" ? "de signature" : e.tsa_status === "failed" ? "d'horodatage" : null;
  if (echec) {
    // Enveloppe terminee : l'operation en echec est le scellement du dossier.
    const quoi = e.status === "completed" ? `Échec ${echec} de l'archivage` : `Échec ${echec}`;
    // Enveloppe close (relecture 3 du 25/09/2026) : une tache encore en file
    // n'y signerait plus rien, et personne n'a forcement ete alerte (une
    // annulation pendant les nouvelles tentatives n'est pas un echec technique).
    if (["cancelled", "declined", "expired"].includes(e.status)) {
      return { texte: `${quoi} — l'enveloppe est close, aucune nouvelle tentative n'aura lieu.`, classe: "alert--error" };
    }
    if (e.reprise_programmee === "nouvelle_tentative") {
      return { texte: `${quoi} — une nouvelle tentative automatique est programmée.`, classe: "alert--error" };
    }
    if (e.reprise_programmee === "reprise_automatique") {
      return {
        texte: `${quoi} — tentatives épuisées, nos équipes sont alertées. Le dossier est relancé automatiquement chaque heure.`,
        classe: "alert--error",
      };
    }
    return {
      texte: `${quoi} — tentatives automatiques épuisées, aucune reprise n'est programmée. Nos équipes sont alertées.`,
      classe: "alert--error",
    };
  }
  if (e.tsa_status === "processing") {
    const n = e.tsa_attempt_count ?? 1;
    return {
      texte: n > 1 ? `Horodatage en cours (${n}ᵉ tentative).` : "Horodatage en cours.",
      classe: "alert--info",
    };
  }
  if (e.hsm_status === "processing") {
    return { texte: "Signature en cours.", classe: "alert--info" };
  }
  if (e.status === "completed" && e.proof_ready === false) {
    return { texte: "Archivage du document signé en cours.", classe: "alert--info" };
  }
  return null;
}

export function EnvelopeDetail({ envelopeId, onRetour, onDupliquer }: Props) {
  const [enveloppe, setEnveloppe] = useState<Detail | null>(null);
  const [audit, setAudit] = useState<EvenementAudit[]>([]);
  const [erreur, setErreur] = useState<unknown>(null);
  const [chargement, setChargement] = useState(true);
  const [action, setAction] = useState(false);
  // B-07 : lien d'une relance dont l'e-mail n'est pas parti.
  const [lienManuel, setLienManuel] = useState<Invitation | null>(null);
  // Pieces jointes deposees par les signataires (12/10/2026) : pour
  // l'emetteur seulement, jamais sur la page publique du dossier.
  const [pieces, setPieces] = useState<{ id: string; libelle: string | null; valeur: string }[]>([]);

  const charger = useCallback(async () => {
    try {
      const [e, a] = await Promise.all([
        api.enveloppe(envelopeId),
        api.auditEnveloppe(envelopeId).catch(() => ({ events: [] })),
      ]);
      setEnveloppe(e);
      setAudit(a.events ?? []);
      void api.champsEnveloppe(envelopeId).then(({ champs }) => setPieces(champs
        .filter((c) => c.kind === "piece_jointe" && c.valeur)
        .map((c) => ({ id: c.id, libelle: c.libelle ?? null, valeur: c.valeur as string }))), () => setPieces([]));
      setErreur(null);
    } catch (e2) {
      setErreur(e2);
    } finally {
      setChargement(false);
    }
  }, [envelopeId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  // Rafraichissement tant que l'enveloppe n'est pas dans un etat final :
  // la signature est traitee en file, l'ecran doit suivre sans que
  // l'utilisateur ait a recharger la page. Terminee sans dossier de preuve
  // (25/09/2026) : le scellement peut durer, parfois des heures s'il
  // echoue - on suit plus lentement.
  useEffect(() => {
    const enCours = enveloppe && !["completed", "cancelled", "declined", "expired"].includes(enveloppe.status);
    const dossierAttendu = enveloppe?.status === "completed" && enveloppe.proof_ready === false;
    if (!enCours && !dossierAttendu) return;
    const id = window.setInterval(() => void charger(), enCours ? 5000 : 30000);
    return () => window.clearInterval(id);
  }, [enveloppe, charger]);

  if (chargement) return <Chargement />;
  if (!enveloppe) return <Erreur erreur={erreur} />;

  const technique = etapeTechnique(enveloppe);
  const terminee = enveloppe.status === "completed";
  // T-33 : l'API renvoie les signataires dans l'ordre propose ; le numero
  // est leur position (le signataire voit le meme : « signataire 2 sur 3 »).
  const plusieurs = (enveloppe.signers ?? []).length > 1;
  const impose = enveloppe.signing_order_imposed ?? false;
  // B-13 : l'approbateur valide sans signer ; Franck, 20/10/2026 : « on ne
  // voit pas le statut des approbateurs ».
  const approbateurs = (enveloppe.signers ?? []).some((s) => s.role === "approver");

  const agir = async (fn: () => Promise<unknown>) => {
    setAction(true);
    setErreur(null);
    try {
      await fn();
      await charger();
    } catch (e) {
      setErreur(e);
    } finally {
      setAction(false);
    }
  };

  return (
    <>
      <button type="button" className="btn btn--link" onClick={onRetour}>
        ← Retour aux enveloppes
      </button>

      <h1 className="page-title" style={{ marginTop: "0.5rem" }}>
        {enveloppe.title}
      </h1>
      <p className="page-lead">
        <Statut valeur={enveloppe.status} /> <Resultat outcome={enveloppe.outcome} motif={enveloppe.outcome_reason} />
        {" "}· créée le {date(enveloppe.created_at, true)}
      </p>
      {/* Porteur, 12/10/2026 : « où est le PDF signé ? » et « bouton en haut ». */}
      <div className="actions" style={{ margin: "0 0 1rem" }}>
        {enveloppe.document_id && (
          <button type="button" className="btn btn--primary" disabled={action}
                  onClick={() => void agir(async () => telecharger(
                    await api.contenuDocument(enveloppe.document_id as string),
                    `${enveloppe.title}${terminee ? " - signe" : ""}.pdf`))}>
            {terminee ? "Télécharger le document signé" : "Télécharger le document"}
          </button>
        )}
        {onDupliquer && (
          // Porteur, 12/10/2026 : tout est repris, rien a ressaisir ; on peut
          // tout modifier avant de lancer.
          <button type="button" className="btn btn--secondary" disabled={action}
                  onClick={() => void agir(async () => onDupliquer(await api.dupliquer(envelopeId)))}>
            Dupliquer l'enveloppe
          </button>
        )}
      </div>

      <Erreur erreur={erreur} />

      {enveloppe.outcome && (
        <div
          className={`alert ${enveloppe.outcome === "success" ? "alert--success" : "alert--error"}`}
          role="status"
          style={{ marginBottom: "1rem" }}
        >
          <strong>{enveloppe.outcome === "success" ? "Succès" : "Échec"}</strong>
          {enveloppe.outcome_reason ? ` — ${enveloppe.outcome_reason}` : ""}
        </div>
      )}

      {technique && (
        <div className={`alert ${technique.classe}`} role="status" style={{ marginBottom: "1rem" }}>
          {technique.texte}
        </div>
      )}

      {/* ------------------------------- lot 2bis : demandes des signataires */}
      {(enveloppe.demandes ?? []).length > 0 && (
        <div className="card" style={{ marginBottom: "1rem" }}>
          <h2 className="card__title" style={{ fontSize: "1.1rem" }}>Demandes des signataires</h2>
          <DemandesSignataires demandes={enveloppe.demandes ?? []} envelopeId={envelopeId}
                               onDecide={() => void charger()} />
        </div>
      )}

      {/* ------------------------------- traitement avant signature */}
      {(enveloppe.intervenants ?? []).length > 0 && (
        <div className="card" style={{ marginBottom: "1rem" }}>
          <h2 className="card__title" style={{ fontSize: "1.1rem" }}>Avant signature</h2>
          <p className="card__lead">
            La signature part quand chacun a terminé et que le document est validé.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Personne</th><th>Rôle</th><th>État</th><th>Le</th><th /></tr>
              </thead>
              <tbody>
                {(enveloppe.intervenants ?? []).map((i) => {
                  const st = STATUTS_INTERVENANT[i.statut] ?? { texte: i.statut, classe: "pill--muted" };
                  return (
                    <tr key={i.id}>
                      <td>{i.full_name || i.email}{i.full_name && <span className="muted"> · {i.email}</span>}</td>
                      <td>{i.role === "remplit" ? "Remplit" : "Valide avant envoi"}</td>
                      <td>
                        <span className={`pill ${st.classe}`}>{st.texte}</span>
                        {i.motif && <span className="muted"> — {i.motif}</span>}
                      </td>
                      <td>{date(i.termine_le ?? i.invite_le, true)}</td>
                      <td className="num">
                        {i.statut === "invite" && enveloppe.status === "prepared" && (
                          <button type="button" className="btn btn--link" disabled={action}
                                  onClick={() => void agir(() => api.relancerIntervenant(envelopeId, i.id))}>
                            Relancer
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------ preuve
          Critique impeccable du 13/10/2026 : la preuve juste apres l'etat et
          les actions, avant le detail des signataires. */}
      <div className="card" style={{ marginBottom: "1rem" }}>
        <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
          Preuve
        </h2>
        {terminee && enveloppe.proof_ready === false ? (
          // L'archive se fait juste apres la fin de l'enveloppe : pas encore
          // d'archivage a renforcer (le passage B-LTA repondrait 409).
          <p className="muted">Archivage du document signé en cours.</p>
        ) : terminee ? (
          <>
            {/* Journal de preuve (13/10/2026) : il est DANS le PDF signe -
                dernieres pages et pieces jointes, scellees avec lui. */}
            <p style={{ marginTop: 0 }}>
              Le journal de preuve est dans le PDF signé (bouton en haut de page) : ses dernières pages
              disent qui a signé, comment et quand, et ses pièces jointes portent la piste d'audit
              complète.
            </p>
            <div className="alert alert--info" style={{ marginTop: "1rem" }}>
              <strong>Renforcer l'archivage longue durée</strong>
              <p style={{ margin: "0.4rem 0 0.7rem" }}>
                Ajoute un horodatage d'archive supplémentaire, recommandé pour une
                conservation de plusieurs années.{" "}
                <strong>Coût : une opération d'horodatage supplémentaire.</strong>
              </p>
              <button
                type="button"
                className="btn btn--secondary"
                disabled={action}
                onClick={() => void agir(() => api.passerEnLta(envelopeId))}
              >
                Renforcer l'archivage
              </button>
            </div>
          </>
        ) : enveloppe.dossier_non_signee === "pret" ? (
          // Un journal de preuve scelle par enveloppe payee, signee ou non (08/10/2026).
          <div className="actions" style={{ marginTop: 0 }}>
            <button type="button" className="btn btn--primary"
                    onClick={() => void api.dossierNonSignee(envelopeId)
                      .then((b) => telecharger(b, `journal-de-preuve-${envelopeId}.pdf`))}>
              Télécharger le journal de preuve
            </button>
          </div>
        ) : enveloppe.dossier_non_signee === "en_preparation" ? (
          <p className="muted">
            Le journal de preuve est en cours de préparation. Il sera disponible ici dès qu'il sera scellé.
          </p>
        ) : (
          <p className="muted">
            Le journal de preuve sera dans le PDF signé, dès que tous les signataires auront signé.
          </p>
        )}
      </div>

      {/* ------------------------------------------- signataires */}
      <div className="card" style={{ marginBottom: "1rem" }}>
        <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
          {approbateurs ? "Signataires et approbateurs" : "Signataires"}
        </h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {plusieurs && <th title={impose ? "Ordre imposé" : "Ordre libre"}>N°</th>}
                <th>{approbateurs ? "Personne" : "Signataire"}</th>
                <th>État</th>
                <th>Consulté</th>
                <th>Vérifié</th>
                <th>{approbateurs ? "Signé / approuvé" : "Signé"}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(enveloppe.signers ?? []).map((s, i) => (
                <tr key={s.id}>
                  {plusieurs && <td className="num">{i + 1}</td>}
                  <td>
                    {s.full_name || s.email}
                    {s.role === "approver" && <> <span className="pill pill--muted">Approbateur</span></>}
                    <div className="muted">{s.email}</div>
                    {s.declined_at && s.decline_reason && (
                      <div className="muted">Motif : {s.decline_reason}</div>
                    )}
                  </td>
                  <td>
                    {impose && !s.invited_at && s.status === "pending" ? (
                      <span className="muted">En attente de son tour</span>
                    ) : s.role === "approver" && s.status === "signed" ? (
                      // Un approbateur n'a pas de signature : il approuve (B-13).
                      <span className="pill pill--ok">Approuvé</span>
                    ) : (
                      <Statut valeur={s.status} />
                    )}
                  </td>
                  <td>{date(s.viewed_at, true)}</td>
                  <td>{date(s.authenticated_at, true)}</td>
                  <td>{date(s.signed_at, true)}</td>
                  <td className="num">
                    {!s.signed_at && !s.declined_at && !s.authenticated_at && !terminee
                      && !(impose && !s.invited_at && s.status === "pending") && (
                      <button
                        type="button"
                        className="btn btn--link"
                        disabled={action}
                        onClick={() =>
                          void agir(async () => {
                            setLienManuel(null);
                            const r = await api.relancerSignataire(envelopeId, s.id);
                            if (!r.email_sent) setLienManuel(r);
                          })
                        }
                      >
                        Relancer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {plusieurs && (
          <p className="muted" style={{ margin: "0.75rem 0 0" }}>
            {impose
              ? approbateurs
                ? "N° : ordre imposé. Chaque personne reçoit son lien quand la précédente a signé ou approuvé."
                : "N° : ordre imposé. Chaque signataire reçoit son lien quand le précédent a signé."
              : "N° : ordre libre. Tous ont reçu leur lien et signent quand ils le souhaitent."}
          </p>
        )}
        {(enveloppe.copy_emails ?? []).length > 0 && (
          <p className="muted" style={{ margin: "0.5rem 0 0" }}>
            Copie du document signé à : {(enveloppe.copy_emails ?? []).join(", ")}
          </p>
        )}
        {lienManuel && (
          <div style={{ marginTop: "0.75rem" }}>
            <LiensATransmettre liens={[lienManuel]} />
          </div>
        )}
      </div>

      {pieces.length > 0 && (
        <div className="card" style={{ marginBottom: "1rem" }}>
          <h2 className="card__title" style={{ fontSize: "1.1rem" }}>Pièces jointes des signataires</h2>
          <ul className="stack-serre" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {pieces.map((p) => (
              <li key={p.id}>
                <button type="button" className="btn btn--link"
                        onClick={() => void api.pieceJointe(envelopeId, p.id).then((b) => telecharger(b, p.valeur))}>
                  {p.libelle ? `${p.libelle} — ` : ""}{p.valeur}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ------------------------------------------ piste d'audit */}
      <div className="card">
        <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
          Piste d'audit
        </h2>
        <p className="card__lead">
          Chaque étape est horodatée et chaînée par empreinte : la suite ne peut pas être
          modifiée sans que cela se voie.
        </p>
        {audit.length === 0 ? (
          <p className="muted">Aucun événement enregistré pour le moment.</p>
        ) : (
          <ul className="timeline">
            {/* Porteur, 12/10/2026 : le plus recent en haut. */}
            {[...audit].reverse().map((ev, i) => (
              <li key={i}>
                <time>{date(ev.occurred_at, true)}</time>
                {EVENEMENTS[ev.event_name] ?? ev.event_name}
                {ev.detail && <span> — {ev.detail}</span>}
                {ev.acteur && <span className="muted"> — {ev.acteur}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {!terminee && enveloppe.status !== "cancelled" && (
        <div className="actions">
          <button
            type="button"
            className="btn btn--danger"
            disabled={action}
            onClick={() => {
              // « Annuler » dans la fenetre rend null : on n'annule RIEN (critique
              // impeccable du 13/10/2026 : l'enveloppe etait annulee quand meme).
              const motif = window.prompt("Motif de l'annulation (facultatif) :");
              if (motif === null) return;
              void agir(() => api.annulerEnveloppe(envelopeId, motif));
            }}
          >
            Annuler cette enveloppe
          </button>
        </div>
      )}
    </>
  );
}
